import { SmartCollectionInput } from 'api/product/getSmartCollection/types';
import { useConfig } from 'config/use-config';
import { SegmentationIds } from 'constants/segmentation';
import useTest10173 from 'hooks/test/use-test-10173';
import { useMemo } from 'react';
import { PetProfile } from 'types/pet-profile';
import {
  generateInputFromCdpPath,
  generateInputFromPetProfile,
  generateInputFromSegmentationIds,
  mergeDynamicInput,
} from 'utils/api/smartCollection';
import { omitNullableHandler } from 'utils/type-helper';
import { getSegmentationFromUrl } from 'utils/url/get-segmentation-from-url';

export interface UseCollectionProps {
  handle: string;
  applyPetProfile: boolean;
  petProfile: PetProfile;
}

interface Output {
  providerProps: {
    smartCollectionInput: SmartCollectionInput;
    trackingListName?: string;
  };
}

/**
 * Generate props for collection context based on the scenario; when the pet
 * profile input is disabled, no segmentation is applied (pure slug input).
 */
const useCollectionProps = ({
  handle,
  applyPetProfile,
  petProfile,
}: UseCollectionProps): Output => {
  const { featureFlags } = useConfig();
  const featureFlg = featureFlags.dynamicUxTest;
  const { isPickyCatsEnabled } = useTest10173();
  const providerProps = useMemo(() => {
    // Wrong usage, it creates a cdp input from anypage
    const cdpInput = generateInputFromCdpPath({
      path: `/collections/${handle}`,
    });

    const isGenericPage = !!cdpInput?.food_types;

    const segment = getSegmentationFromUrl({ path: `/collections/${handle}` });

    if ((!segment && !isGenericPage) || !featureFlg) {
      return { smartCollectionInput: { slug: handle } };
    }

    try {
      if (!isGenericPage) {
        const input = generateInputFromSegmentationIds({
          segmentationIds: [segment?.id as SegmentationIds],
        });

        return {
          smartCollectionInput: { slug: handle, ...input },
        };
      }

      if (
        // assign pet profile in generic page if user wants to
        // applyPetProfile should be AFTER first check because this state is not reset
        // after page change
        applyPetProfile &&
        Object.values(petProfile).flat().filter(omitNullableHandler).length > 0
      ) {
        const inputFromPet = generateInputFromPetProfile({
          petProfile,
          includeDislikedProteins: isPickyCatsEnabled,
          omitFoodType: true,
        });
        const smartCollectionInput = mergeDynamicInput({
          target: cdpInput,
          source: inputFromPet,
        });
        delete smartCollectionInput.food_types;
        delete smartCollectionInput.protein_tags;
        delete smartCollectionInput.wet_food_tags;
        return {
          smartCollectionInput: {
            ...smartCollectionInput,
            slug: handle,
          },
        };
      }

      return { smartCollectionInput: { slug: handle } };
    } catch (error) {
      // fall back for edge cases as there's no point in crushing web page
      console.warn(error);
      return { smartCollectionInput: { slug: handle } };
    }
  }, [applyPetProfile, handle, petProfile, featureFlg, isPickyCatsEnabled]);

  return { providerProps };
};

export default useCollectionProps;
